import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Building2, DollarSign, FolderPlus, Inbox, MessagesSquare, Search, Sparkles, Users } from "lucide-react";
import { usePermission } from "@ncp/api-client";
import { PlatformIcon, tileClasses } from "@ncp/assistant-sdk";
import { formatNumber, useLocale, useTranslation } from "@ncp/i18n";
import { slugFromLabel } from "@ncp/forms";
import { Badge, Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, PageHeader, Progress, Skeleton, StatCard, cn, notifySaved } from "@ncp/ui";
import { useBreadcrumbs, useHelpTopic, usePaletteActions } from "../app/console-context";
import { EMPTY_WORKSPACE, useWorkspaceActions, useWorkspaces, type Workspace, type WorkspaceKind } from "../features/workspaces/api";
import { ColorPicker } from "../features/workspaces/RefPicker";

const usd = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: n >= 100 ? 0 : 2 })}`;

/** Workspaces: the unit of scale of the agentic platform – one card per department or function with live usage. */
export function WorkspacesPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const canWrite = can("platform.workspaces.write");
  const [creating, setCreating] = useState(false);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const workspaces = useWorkspaces();

  useBreadcrumbs(useMemo(() => [{ label: t("admin:nav.workspaces") }], [t]));
  useHelpTopic({ title: t("admin:workspaces.title"), docsAnchor: "workspaces", body: (<><p>{t("admin:workspaces.help.what")}</p><p>{t("admin:workspaces.help.membership")}</p><p>{t("admin:workspaces.help.budget")}</p></>) });
  usePaletteActions(useMemo(() => canWrite ? [{ id: "workspaces.new", label: t("admin:workspaces.newWorkspace"), icon: FolderPlus, permission: "platform.workspaces.write", run: () => setCreating(true) }] : [], [canWrite, t]));

  const all = workspaces.data ?? [];
  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return all.filter((w) => (!kind || w.kind === kind) && (!term || w.name.toLowerCase().includes(term) || w.key.includes(term) || (w.nameAr ?? "").includes(term)));
  }, [all, kind, q]);
  const totals = useMemo(() => ({
    members: all.reduce((n, w) => n + w.usage.members, 0),
    threads: all.reduce((n, w) => n + w.usage.threadsThisWeek, 0),
    spend: all.reduce((n, w) => n + w.usage.spendMonthUsd, 0),
    pending: all.reduce((n, w) => n + w.usage.pendingProposals, 0),
  }), [all]);

  return (
    <div>
      <PageHeader title={t("admin:workspaces.title")} description={t("admin:workspaces.subtitle")} actions={canWrite ? <Button variant="gold" onClick={() => setCreating(true)}><FolderPlus /> {t("admin:workspaces.newWorkspace")}</Button> : undefined} />

      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label={t("admin:workspaces.stats.workspaces")} value={all.filter((w) => w.enabled).length} unit={all.length !== all.filter((w) => w.enabled).length ? `/ ${all.length}` : undefined} icon={Building2} tone="gold" footer={t("admin:workspaces.stats.workspacesHint")} />
        <StatCard label={t("admin:workspaces.stats.members")} value={totals.members} icon={Users} tone="sea" footer={t("admin:workspaces.stats.membersHint")} />
        <StatCard label={t("admin:workspaces.stats.threadsWeek")} value={totals.threads} icon={MessagesSquare} tone="camel" footer={t("admin:workspaces.stats.threadsWeekHint")} />
        <StatCard label={t("admin:workspaces.stats.spendMonth")} value={totals.spend} format={usd} icon={DollarSign} tone="neutral" footer={t("admin:workspaces.stats.pendingHint", { count: totals.pending })} />
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative"><Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin:workspaces.searchPlaceholder")} className="h-9 w-64 ps-8" /></div>
        <NativeSelect value={kind} className="h-9 w-44" onChange={(e) => setKind(e.target.value)}><option value="">{t("admin:workspaces.fields.kind")}: {t("admin:common.all")}</option><option value="Department">{t("admin:workspaces.kind.Department")}</option><option value="Function">{t("admin:workspaces.kind.Function")}</option></NativeSelect>
      </div>

      {workspaces.isLoading ? <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3"><Skeleton className="h-52" /><Skeleton className="h-52" /><Skeleton className="h-52" /></div> : rows.length === 0 ? (
        <EmptyState icon={Building2} title={all.length === 0 ? t("admin:workspaces.emptyTitle") : t("admin:workspaces.noMatch")} description={all.length === 0 ? t("admin:workspaces.emptyBody") : undefined} action={canWrite && all.length === 0 ? <Button variant="gold" onClick={() => setCreating(true)}><FolderPlus /> {t("admin:workspaces.newWorkspace")}</Button> : undefined} />
      ) : (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((w) => <WorkspaceCard key={w.key} w={w} locale={locale} onOpen={() => navigate(`/workspaces/${encodeURIComponent(w.key)}`)} />)}
        </div>
      )}

      <CreateWorkspaceDialog open={creating} onOpenChange={setCreating} existing={all} onCreated={(key) => navigate(`/workspaces/${encodeURIComponent(key)}`)} />
    </div>
  );
}

export function WorkspaceCard({ w, locale, onOpen }: { w: Workspace; locale: string; onOpen: () => void }) {
  const { t } = useTranslation();
  const name = locale === "ar" && w.nameAr ? w.nameAr : w.name;
  const description = locale === "ar" && w.descriptionAr ? w.descriptionAr : w.description;
  const budgetPct = w.monthlyBudgetUsd > 0 ? Math.min(100, Math.round((w.usage.spendMonthUsd / w.monthlyBudgetUsd) * 100)) : null;
  return (
    <button type="button" onClick={onOpen} className={cn("group rounded-2xl border border-sand-200 bg-white/80 p-4 text-start transition-colors hover:border-aegold-300 hover:bg-aegold-50/30", !w.enabled && "opacity-60")}>
      <div className="flex items-start gap-3">
        <span className={tileClasses(w.color, "size-11")}><PlatformIcon name={w.icon} fallback={Building2} className="size-5" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <h3 className="truncate font-semibold">{name}</h3>
            <Badge variant="outline" size="sm">{t(`admin:workspaces.kind.${w.kind}`)}</Badge>
            {!w.enabled ? <Badge variant="default" size="sm">{t("admin:common.disabled")}</Badge> : null}
            {w.sourcePackageKey ? <Badge variant="camel" size="sm">{w.sourcePackageKey}</Badge> : null}
          </div>
          <p className="text-xs text-muted-foreground"><code className="font-mono" dir="ltr">{w.key}</code>{w.parentKey ? ` · ${t("admin:workspaces.fields.parent")}: ${w.parentKey}` : ""}</p>
        </div>
      </div>
      {description ? <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">{description}</p> : null}
      <dl className="mt-3 grid grid-cols-4 gap-2 text-center">
        <Metric icon={Sparkles} label={t("admin:workspaces.metrics.assistants")} value={w.assistantKeys.length} />
        <Metric icon={Users} label={t("admin:workspaces.metrics.members")} value={w.usage.members} hint={w.memberRoleKeys.length ? `${w.memberRoleKeys.length} ${t("admin:workspaces.metrics.roles")}` : undefined} />
        <Metric icon={MessagesSquare} label={t("admin:workspaces.metrics.threads")} value={w.usage.threads} hint={t("admin:workspaces.metrics.thisWeek", { count: w.usage.threadsThisWeek })} />
        <Metric icon={Inbox} label={t("admin:workspaces.metrics.pending")} value={w.usage.pendingProposals} tone={w.usage.pendingProposals ? "warning" : undefined} />
      </dl>
      <div className="mt-3">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground"><span>{t("admin:workspaces.metrics.spendMonth")}</span><span className="tabular">{usd(w.usage.spendMonthUsd)}{w.monthlyBudgetUsd > 0 ? ` / ${usd(w.monthlyBudgetUsd)}` : ""}</span></div>
        {budgetPct !== null ? <Progress value={budgetPct} className="mt-1 h-1.5" tone={budgetPct >= 90 ? "danger" : budgetPct >= 70 ? "warning" : "gold"} /> : <p className="mt-1 text-[10px] text-muted-foreground">{t("admin:workspaces.metrics.noBudget")}</p>}
      </div>
    </button>
  );
}

function Metric({ icon: Icon, label, value, hint, tone }: { icon: typeof Users; label: string; value: number; hint?: string; tone?: "warning" }) {
  const { locale } = useLocale();
  return (
    <div className="rounded-lg bg-sand-50 px-1 py-1.5">
      <dt className="flex items-center justify-center gap-1 text-[10px] text-muted-foreground"><Icon className="size-3" /> {label}</dt>
      <dd className={cn("text-sm font-semibold tabular", tone === "warning" && value > 0 && "text-warning-700")}>{formatNumber(value, locale)}</dd>
      {hint ? <dd className="truncate text-[10px] text-muted-foreground">{hint}</dd> : null}
    </div>
  );
}

function CreateWorkspaceDialog({ open, onOpenChange, existing, onCreated }: { open: boolean; onOpenChange: (o: boolean) => void; existing: Workspace[]; onCreated: (key: string) => void }) {
  const { t } = useTranslation();
  const actions = useWorkspaceActions();
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [kind, setKind] = useState<WorkspaceKind>("Department");
  const [parentKey, setParentKey] = useState("");
  const [color, setColor] = useState("sand");
  const effectiveKey = keyTouched ? key : slugFromLabel(name);
  const keyError = !effectiveKey ? null : !/^[a-z][a-z0-9-]{1,63}$/.test(effectiveKey) ? t("admin:workspaces.create.keyInvalid") : existing.some((w) => w.key === effectiveKey) ? t("admin:workspaces.create.keyTaken") : null;
  const ready = name.trim().length > 1 && effectiveKey && !keyError;
  const reset = () => { setName(""); setNameAr(""); setKey(""); setKeyTouched(false); setKind("Department"); setParentKey(""); setColor("sand"); };

  const create = async () => {
    try {
      const w = await actions.create.mutateAsync({ key: effectiveKey, ...EMPTY_WORKSPACE, name: name.trim(), nameAr: nameAr.trim() || null, kind, parentKey: parentKey || null, color });
      notifySaved(w, t("admin:workspaces.create.created"));
      onOpenChange(false); reset();
      onCreated(w.key);
    } catch { /* useSave reported it */ }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent size="md">
        <DialogHeader><DialogTitle>{t("admin:workspaces.newWorkspace")}</DialogTitle><DialogDescription>{t("admin:workspaces.create.intro")}</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t("admin:common.name")} required><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("admin:workspaces.create.namePlaceholder")} autoFocus /></Field>
          <Field label={t("admin:common.nameAr")}><Input value={nameAr} dir="rtl" onChange={(e) => setNameAr(e.target.value)} /></Field>
          <Field label={t("admin:common.key")} hint={t("admin:workspaces.fields.keyHint")} error={keyError ?? undefined}><Input value={effectiveKey} dir="ltr" className="font-mono" onChange={(e) => { setKeyTouched(true); setKey(e.target.value.toLowerCase()); }} /></Field>
          <Field label={t("admin:workspaces.fields.kind")}><NativeSelect value={kind} onChange={(e) => setKind(e.target.value as WorkspaceKind)}><option value="Department">{t("admin:workspaces.kind.Department")}</option><option value="Function">{t("admin:workspaces.kind.Function")}</option></NativeSelect></Field>
          <Field label={t("admin:workspaces.fields.parent")} hint={t("admin:workspaces.fields.parentHint")}><NativeSelect value={parentKey} onChange={(e) => setParentKey(e.target.value)}><option value="">—</option>{existing.map((w) => <option key={w.key} value={w.key}>{w.name}</option>)}</NativeSelect></Field>
          <Field label={t("admin:workspaces.fields.color")}><ColorPicker value={color} onChange={setColor} /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" disabled={!ready} loading={actions.create.isPending} onClick={create}><FolderPlus /> {t("admin:workspaces.create.createOpen")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
